/**
 * 前端埋点(《埋点与统计口径》§6):排队,每 5 秒 / 攒满 20 条 / 页面切走时批量发 /v1/track(fetch keepalive)。
 * 事件名与属性以 @cg/shared 登记为准,服务端校验、采样、限流;这里只管送达,失败不重试、不报错(丢了不影响业务)。
 * 匿名会话 id 存 sessionStorage(一个标签页一次会话,UV 按它去重);不采集任何个人信息(§5)。
 */

export interface TrackContext {
  /** 站点码(x-tenant-code);官网用主办方站点码,平台 / 机构用当前站点 */
  tenantCode?: string | undefined;
  /** 登录令牌(有就记到账号上) */
  accessToken?: string | undefined;
  /** 端标识(x-client):学生端在手机上是 h5,不给按 web;服务端记到事件的 client 维度 */
  client?: 'web' | 'h5' | undefined;
}

interface Pending {
  name: string;
  props: Record<string, unknown>;
  at: string;
  page?: string;
  ctx: TrackContext;
}

const FLUSH_MS = 5000;
const FLUSH_AT = 20;
let queue: Pending[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let defaults: () => TrackContext = () => ({});
let cachedSession: string | null = null;

/** 本标签页的匿名会话 id(8–40 位字母数字下划线横线) */
export function trackSession(): string {
  if (cachedSession) return cachedSession;
  let v: string | null = null;
  try {
    v = sessionStorage.getItem('cg.ts');
  } catch {
    /* 隐私模式 */
  }
  if (!v || !/^[A-Za-z0-9_-]{8,40}$/.test(v)) {
    const bytes = new Uint8Array(15);
    crypto.getRandomValues(bytes);
    v = btoa(String.fromCharCode(...bytes))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
    try {
      sessionStorage.setItem('cg.ts', v);
    } catch {
      /* 隐私模式:本页内存里用 */
    }
  }
  cachedSession = v;
  return v;
}

/** 默认上下文(应用启动时接上主会话的站点码与令牌) */
export function configureTracker(ctx: () => TrackContext): void {
  defaults = ctx;
}

/** 记一条前端事件(属性从 client-events.ts 的构造表取:`trackEvent(clientEvents.xxx(...))`) */
export function trackEvent(
  e: { name: string; props: Record<string, unknown> },
  opts: TrackContext & { page?: string } = {},
): void {
  track(e.name, e.props, opts);
}

/** 记一条前端事件 */
export function track(
  name: string,
  props: Record<string, unknown> = {},
  opts: TrackContext & { page?: string } = {},
): void {
  if (typeof window === 'undefined') return;
  const d = defaults();
  queue.push({
    name,
    props,
    at: new Date().toISOString(),
    page: opts.page ?? location.pathname.slice(0, 64),
    ctx: {
      tenantCode: opts.tenantCode ?? d.tenantCode,
      accessToken: opts.accessToken ?? d.accessToken,
      client: opts.client ?? d.client,
    },
  });
  if (queue.length >= FLUSH_AT) flushTrack();
  else if (!timer) timer = setTimeout(flushTrack, FLUSH_MS);
}

/** 立刻发出去(按站点 × 令牌 × 端分组,一组一个请求) */
export function flushTrack(): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (!queue.length) return;
  const batch = queue;
  queue = [];
  const groups = new Map<string, Pending[]>();
  for (const p of batch) {
    const k = `${p.ctx.tenantCode ?? ''}|${p.ctx.accessToken ?? ''}|${p.ctx.client ?? 'web'}`;
    groups.set(k, [...(groups.get(k) ?? []), p]);
  }
  for (const list of groups.values()) {
    const { tenantCode, accessToken, client } = list[0]!.ctx;
    const headers: Record<string, string> = {
      'content-type': 'application/json',
      'x-client': client ?? 'web',
    };
    if (tenantCode) headers['x-tenant-code'] = tenantCode;
    if (accessToken) headers['authorization'] = `Bearer ${accessToken}`;
    const body = JSON.stringify({
      session: trackSession(),
      events: list.slice(0, 50).map((p) => ({ name: p.name, props: p.props, at: p.at, page: p.page })),
    });
    void fetch('/v1/track', { method: 'POST', headers, body, keepalive: true }).catch(() => undefined);
  }
}

/** 页面切走 / 关闭前发掉剩下的 */
export function installTrackFlush(): void {
  if (typeof document === 'undefined') return;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushTrack();
  });
  window.addEventListener('pagehide', flushTrack);
}

/** 报错指纹:只发消息的短哈希,不发堆栈与原文(可能含个人信息) */
export function messageHash(msg: string): string {
  let h = 5381;
  for (let i = 0; i < msg.length; i++) h = ((h << 5) + h + msg.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

/** 外站来源只留域名(不带路径和参数) */
export function referrerHost(ref: string, self: string): string | undefined {
  try {
    const u = new URL(ref);
    return u.host && u.host !== self ? u.host.slice(0, 80) : undefined;
  } catch {
    return undefined;
  }
}
